import { Component, inject, OnInit, signal } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { BaseAbstractPage, GeolocationService } from '@incust-mobile/core';
import { POSApiService, PosApiModel } from '@incust-mobile/api';
import { firstValueFrom, forkJoin } from 'rxjs';

const SOCIAL_LINKS = ['facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'tiktok'] as const;
type SocialKey = typeof SOCIAL_LINKS[number];
const SOCIAL_ICONS: Record<SocialKey, string> = {
  facebook: 'logo-facebook',
  instagram: 'logo-instagram',
  twitter: 'logo-twitter',
  linkedin: 'logo-linkedin',
  youtube: 'logo-youtube',
  tiktok: 'logo-tiktok'
};

@Component({
  selector: 'app-pos-detail-page',
  templateUrl: 'pos-detail.page.html',
  styleUrls: ['pos-detail.page.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, TranslatePipe]
})
export class PosDetailPage extends BaseAbstractPage implements OnInit {
  private route = inject(ActivatedRoute);
  private posApi = inject(POSApiService);
  private geo = inject(GeolocationService);

  isLoading = signal(true);
  pos = signal<PosApiModel | null>(null);
  branches = signal<PosApiModel[]>([]);
  distance = signal<number | null>(null);

  readonly socialLinks = SOCIAL_LINKS;
  readonly socialIcons = SOCIAL_ICONS;

  constructor() {
    super();
  }

  async ngOnInit() {
    const posId = this.route.snapshot.paramMap.get('posId');
    if (!posId) {
      await this.router.navigate(['/home/pos-list']);
      return;
    }
    await this.load(posId);
  }

  goBack() {
    this.router.navigate(['/home/pos-list']);
  }

  makeCall(number: string) {
    window.open('tel:' + number, '_system');
  }

  openBrowser(url: string) {
    window.open(url, '_system');
  }

  openInMaps() {
    const p = this.pos();
    if (!p?.latitude || !p?.longitude) return;
    const url = `https://maps.google.com/?q=${p.latitude},${p.longitude}`;
    window.open(url, '_system');
  }

  goToBranch(id: string) {
    this.router.navigate(['/home/pos-detail', id]);
  }

  getSocialUrl(key: SocialKey): string | null {
    const links = (this.pos() as any)?.links;
    return links?.[key] ?? null;
  }

  getBranchDistance(branch: PosApiModel): number | null {
    if (!branch.latitude || !branch.longitude) return null;
    const d = this.geo.distanceTo(branch.latitude, branch.longitude);
    return d === Infinity ? null : d;
  }

  private async load(posId: string) {
    this.isLoading.set(true);
    try {
      const pos = await firstValueFrom(
        this.posApi.incustControllersClientClientPosDetail(posId)
      );
      this.pos.set(pos);

      const coords = this.geo.coords();
      if (coords && pos.latitude && pos.longitude) {
        const d = this.geo.distanceTo(pos.latitude, pos.longitude);
        this.distance.set(d === Infinity ? null : d);
      }

      if (pos.loyalty_id) {
        const all = await firstValueFrom(
          this.posApi.incustControllersClientClientPos(pos.loyalty_id)
        );
        this.branches.set((all ?? []).filter((b) => b.id !== posId));
      }
    } catch {
      await this.notificationService.show(
        'app#city_card#pos_detail#load_error',
        2000,
        'top',
        'danger'
      );
    } finally {
      this.isLoading.set(false);
    }
  }
}
